import { createElement } from "react";
import { datasetFormRequest } from "./dataset-form-signal";
import type { FormItem } from "./form-builder.model";
import type { FormAction, FormActionResult, FormSection } from "./renderer/form-renderer";
import { importFormModule } from "./form-module";
import { LookupCache } from "../../services/lookup-cache";
import { notify } from "../notifications/NotificationProvider";
import { adminRpc } from "../../services/admin-rpc-client";
import { errorMessage } from "../../utils/error-message";
import { provisionMonitor } from "./monitor-provision";
import { listSecrets, listSecretTypes, type Secret, type SecretType } from "../../services/secret-admin-rpc-client";
import {
  executionUserProblem,
  hasLevel,
  isRootWorker,
  type KnownWorker,
  listWorkers,
  pluginColumns,
  pluginFromDecls,
  pluginExtraData,
  pluginJobPayload,
  type PluginMessage,
  type PluginWorker,
  type RegisteredPlugin,
  resolvePlugin,
  resolvePluginByUid,
  runPluginFunction, callPluginFunction,
  sandboxAccountsFor,
  validatePluginConfig,
  workerLabel,
  workerOptionText,
  workersWithRunner,
} from "./monitor-plugin-config";
import { describeRunner, type RunnerDescription } from "../../services/daemon-job-rpc-client";
import { itemAt, itemsFor } from "./type-tree-form";
import { defaultsNote, readTypeTree } from "./type-tree";
import { CheckpointVmError } from "../../checkpoint-vm/checkpoint-vm";
import { planOf, type LoadedPlan } from "../../checkpoint-vm/plans";
import type { ValidationIssue } from "./validation";

// The create-and-edit dialog of a monitor that points at a checkpoint monitor plugin, any plugin: the
// plugin's configuration is a form generated from its configuration type (the tree its fileset's
// registration carries, type-tree-form.ts), and around it the rows a plugin monitor needs: the worker
// it collects from, its name, cadence and alert channel. Validate runs the plugin's validate on the
// worker with the configuration being saved: an Error refuses the save, a Warn needs "save with
// warnings" ticked. "Try it" runs the plugin's collect on the worker with the form's values, saving
// nothing, and shows the readings in the form. The row graph is the one `a8 monitor enroll plugin`
// writes. In edit, the plugin is fixed: "New monitor like this" opens the create form seeded with the
// monitor's values instead. A fileset that carries a form module beside the plugin's module
// (`<module>.form.ts`, compiled at push; form-sdk.ts) has that module shape the configuration's
// sections and add actions, starting from the generated form; a module that fails to load falls
// back to the generated form and says so.
// FEATURE-20261021-check-config-type-and-fileset-form.

const ALERT_CHANNEL_LOOKUP_KEY = "continuum:alertchannels";
const CONFIG = "config";

export interface PluginMonitorFormTarget {
  /** the fileset declaring the plugin, by name or by uid */
  fileset?: string;
  filesetUid?: string;
  /** the plugin's name in that fileset */
  plugin: string;
  /** a monitor to edit; none creates */
  monitorUid?: string;
  /** form values a new monitor starts from ("New monitor like this"); create only */
  like?: Record<string, unknown>;
}

function registerLookups() {
  LookupCache.register({
    key: ALERT_CHANNEL_LOOKUP_KEY,
    sql: "from alertchannel where enabled = true select uid, name",
    valueField: "uid",
    labelField: "name",
  });
}

function parse(v: unknown): unknown {
  let cur = v;
  for (let i = 0; i < 3 && typeof cur === "string"; i++) {
    try {
      cur = JSON.parse(cur);
    } catch {
      break;
    }
  }
  return cur;
}

type Existing = { monitor: Record<string, unknown>; job: Record<string, unknown> | undefined };

async function loadExisting(monitorUid: string | undefined): Promise<Existing | undefined> {
  if (!monitorUid) return undefined;
  const esc = monitorUid.replace(/'/g, "''");
  const m = await adminRpc.query(
    `SELECT uid, enabled, channel_uids, extra_data, expected_interval_seconds FROM monitor WHERE uid = '${esc}'`,
  );
  if (m.rows.length === 0) return undefined;
  const j = await adminRpc.query(`SELECT uid, name, workeruid, payload_json FROM job WHERE monitor_uid = '${esc}' LIMIT 1`);
  return { monitor: m.rows[0] as Record<string, unknown>, job: j.rows[0] as Record<string, unknown> | undefined };
}

function fail(message: string) {
  notify({ severity: "error", title: "Not saved", message });
}

/** The messages validate answered, as one line each: "seeds[0]: give a url". */
function messageLines(messages: PluginMessage[], level: string): string {
  return messages
    .filter((m) => m.level === level)
    .map((m) => (m.path ? `${m.path}: ${m.text}` : m.text))
    .join("; ");
}

/** The configuration items: generated from the plugin's type, or one JSON box for a registration without a type. */
export function configItems(plugin: RegisteredPlugin, types: SecretType[], secrets: Secret[]): FormItem[] {
  if (plugin.config) return itemsFor(plugin.config, CONFIG, "", { types, secrets }, { required: true, hasDefault: false });
  return [
    {
      name: CONFIG,
      type: "json",
      label: "Configuration",
      required: true,
      helpText: "The plugin's configuration as JSON. This registration carries no configuration type; push the fileset again for a form.",
    },
  ];
}

/**
 * The configuration being saved, from the form's values: assembled by the checkpoint vm from the plugin's
 * type, which throws a CheckpointVmError at the field whose value is not of its type.
 */
export function configOf(plugin: RegisteredPlugin, values: Record<string, unknown>, loaded?: LoadedPlan): unknown {
  if (plugin.config && loaded) return loaded.vm.fromForm(loaded.handle, plugin.name, values, CONFIG);
  const raw = values[CONFIG];
  if (typeof raw !== "string") return raw ?? {};
  return raw.trim() === "" ? {} : JSON.parse(raw);
}

const SEVERITY: Record<string, ValidationIssue["severity"]> = { Error: "error", Warn: "warning", Info: "info" };

/**
 * What validate answered, for the form: one line, and each message under the field its path names
 * (a message about seeds[1] lands on the seeds box, prefixed; one naming no field stays in the line).
 */
export function validatedResult(plugin: RegisteredPlugin, messages: PluginMessage[], values: Record<string, unknown>): FormActionResult {
  const issues: ValidationIssue[] = [];
  const top: PluginMessage[] = [];
  for (const m of messages) {
    const at = plugin.config && m.path ? itemAt(plugin.config, m.path, CONFIG, values) : undefined;
    if (at) issues.push({ severity: SEVERITY[m.level] ?? "info", message: at.rest ? `${at.rest}: ${m.text}` : m.text, fields: [at.name] });
    else top.push(m);
  }
  const line = (level: string) => messageLines(top, level);
  if (hasLevel(messages, "Error")) return { severity: "error", message: `Validate found errors${line("Error") ? ": " + line("Error") : ""}`, issues };
  if (hasLevel(messages, "Warn")) return { severity: "info", message: `Validate answered warnings${line("Warn") ? ": " + line("Warn") : ""}. Tick Save with warnings to save anyway.`, issues };
  return { severity: "success", message: line("Info") || (issues.length ? "Valid." : "The configuration is valid."), issues };
}

/** A configuration the vm could not assemble, for the form: the message, on the field it is about. */
export function unassembled(e: unknown): FormActionResult {
  const message = errorMessage(e) || String(e);
  if (e instanceof CheckpointVmError && e.path) return { severity: "error", message: `The configuration is not valid: ${message}`, issues: [{ severity: "error", message, fields: [e.path] }] };
  return { severity: "error", message: `The configuration is not valid: ${message}` };
}

/** What collect answered, as one line for the form: the readings, or why it failed. */
export function collectedLine(result: unknown): FormActionResult {
  const r = result as { case?: unknown; value?: unknown; failure?: { reason?: unknown } } | null;
  if (!r || typeof r !== "object" || typeof r.case !== "string") {
    return { severity: "error", message: `collect answered ${JSON.stringify(result)}, not a Result.` };
  }
  if (r.case !== "Ok") return { severity: "error", message: `collect failed: ${String(r.failure?.reason ?? "no reason given")}` };
  const readings = Array.isArray(r.value) ? (r.value as Array<{ key?: unknown; scope?: unknown; value?: unknown; unit?: unknown }>) : [];
  if (readings.length === 0) return { severity: "info", message: "collect answered no readings." };
  const lines = readings.map((x) => {
    const scope = x.scope ? ` ${String(x.scope)}` : "";
    const unit = x.unit ? ` ${String(x.unit)}` : "";
    return `${String(x.key)}${scope} = ${String(x.value)}${unit}`;
  });
  return { severity: "success", message: `${readings.length} reading${readings.length === 1 ? "" : "s"}: ${lines.join("; ")}` };
}

/** The form's values for a stored configuration, with what its type's completion fills in, laid out by the checkpoint vm. */
export function configValues(plugin: RegisteredPlugin, stored: unknown, out: Record<string, unknown>, loaded?: LoadedPlan): Record<string, unknown> {
  if (plugin.config && loaded) return Object.assign(out, loaded.vm.toForm(loaded.handle, plugin.name, loaded.vm.defaults(loaded.handle, plugin.name, stored ?? {}), CONFIG));
  out[CONFIG] = JSON.stringify(stored ?? {}, null, 2);
  return out;
}

export function showPluginMonitorForm(target: PluginMonitorFormTarget) {
  (async () => {
    registerLookups();
    let plugin: RegisteredPlugin;
    let types: SecretType[];
    let secrets: Secret[];
    let workers: KnownWorker[];
    let described: Map<string, RunnerDescription>;
    let existing: Existing | undefined;
    try {
      [plugin, types, secrets, , workers, existing] = await Promise.all([
        target.filesetUid ? resolvePluginByUid(target.filesetUid, target.plugin) : resolvePlugin(target.fileset ?? "", target.plugin),
        listSecretTypes(),
        listSecrets(),
        LookupCache.ensure(ALERT_CHANNEL_LOOKUP_KEY),
        listWorkers(),
        loadExisting(target.monitorUid),
      ]);
      // each worker's runner says what it is (jobrunner.v1.Describe); one the registry does not list is
      // down. A runner that is listed but does not answer (from before Describe, or the call refused) is
      // judged by its job-runner run instead, and its accounts guessed from the server's logins.
      described = new Map();
      const unanswered: string[] = [];
      const answers = await Promise.allSettled(workers.map((w) => describeRunner(w.uid)));
      answers.forEach((a, i) => {
        if (a.status === "fulfilled" && a.value) described.set(workers[i]!.uid, a.value);
        else if (a.status === "rejected") unanswered.push(workers[i]!.uid);
      });
      if (unanswered.length > 0) {
        const up = await workersWithRunner().catch(() => new Set<string>());
        for (const uid of unanswered) if (up.has(uid)) described.set(uid, { workerUid: uid, sandboxed: false, allowedUsers: [], policyKnown: false, version: "" });
      }
    } catch (e) {
      notify({ severity: "error", title: "Could not open the monitor form", message: (errorMessage(e) || String(e)) });
      return;
    }
    // the plugin's plan in the tab's checkpoint vm: the form's plugin is the plan's declaration of it
    // (its members, its configuration type), and the vm reads and writes its configuration, so what
    // the form runs on the worker and what it saves come from one plan. The fileset's registration
    // (extra_data.pluginDecls) only found it.
    let loaded: LoadedPlan;
    try {
      loaded = await planOf(plugin.filesetUid);
      const declared = pluginFromDecls(plugin.filesetUid, loaded.revisionUid, loaded.vm.plugins(loaded.handle), plugin.name);
      if (!declared) throw new Error(`the fileset's current plan declares no plugin ${plugin.name}`);
      const config = declared.config ? readTypeTree(loaded.vm.typeTree(loaded.handle, plugin.name)) : undefined;
      plugin = { ...declared, ...(plugin.form ? { form: plugin.form } : {}), ...(config ? { config } : {}) };
      if (!config) delete plugin.config;
    } catch (e) {
      notify({ severity: "error", title: "Could not open the monitor form", message: `The plugin's program did not load: ${errorMessage(e) || String(e)}` });
      return;
    }
    if (target.monitorUid && !existing) {
      notify({ severity: "error", title: "Monitor not found", message: `Monitor ${target.monitorUid} could not be loaded.` });
      return;
    }
    const workerLabels = new Map(workers.map((w) => [w.uid, workerLabel(w)]));
    const labelOf = (uid: unknown) => workerLabels.get(String(uid ?? "")) ?? "";
    const runners = new Set(described.keys());
    // a root worker runs a job only as a sandboxed account: the ones its runner's host policy allows,
    // which the runner answers; a runner that does not know its policy (socket-fed, or from before
    // Describe) leaves the logins of the server's other workers as the guess
    const accountsFor = (vals: Record<string, unknown>) => {
      const uid = String(vals["worker"] ?? "");
      const d = described.get(uid);
      if (d?.policyKnown) return [...d.allowedUsers].sort();
      return sandboxAccountsFor(workers, uid);
    };
    const accountProblem = (vals: Record<string, unknown>): string => {
      if (!isRootWorker(labelOf(vals["worker"]))) return "";
      const accounts = accountsFor(vals);
      const chosen = String(vals["executionUser"] ?? "");
      if (accounts.length === 0) return `${labelOf(vals["worker"])}'s runner allows no account to run jobs as: give its host policy one.`;
      if (!accounts.includes(chosen)) return `Pick the account the jobs run as: one of ${accounts.join(", ")}.`;
      return "";
    };
    const workerOf = (vals: Record<string, unknown>): PluginWorker => ({
      uid: String(vals["worker"] ?? ""),
      executionUser: isRootWorker(labelOf(vals["worker"])) ? String(vals["executionUser"] ?? "") : "",
    });
    // the picker lists runners: a worker with no runner up runs nothing, so it is not offered. The one
    // exception is the worker a monitor being edited already has, kept so the edit does not lose it;
    // an action refuses it at once rather than waiting a minute for the mesh to say nothing ran.
    const savedWorker = String(existing?.job?.["workeruid"] ?? "");
    const workerOptions = [...workerLabels.entries()]
      .filter(([uid]) => runners.has(uid) || uid === savedWorker)
      .map(([uid, label]) => ({ value: uid, text: workerOptionText(label, runners.has(uid)) }))
      .sort((a, b) => a.text.localeCompare(b.text));
    const noRunner = (vals: Record<string, unknown>): string => {
      const uid = String(vals["worker"] ?? "");
      if (!uid) return "Pick a runner: the plugin's functions run on the monitor's runner.";
      return runners.has(uid) ? "" : `${labelOf(uid)} has no runner up; pick a runner that is.`;
    };
    const pluginTitle = plugin.title || plugin.name;

    const extra = (parse(existing?.monitor["extra_data"]) ?? {}) as { config?: unknown };
    const channels = parse(existing?.monitor["channel_uids"]);
    const interval = Number(existing?.monitor["expected_interval_seconds"] ?? 300);
    const jobPayload = (parse(existing?.job?.["payload_json"]) ?? {}) as { executionUser?: string };

    // what validate last answered, for the configuration it was asked about
    let validated: { json: string; messages: PluginMessage[] } = { json: "", messages: [] };

    const formItems: FormItem[] = [
      {
        name: "worker",
        type: "select",
        label: "Runner",
        required: true,
        helpText: "The runner the monitor collects from; Validate and Try it run on it. Only runners that are up are listed.",
        options: workerOptions,
      },
      {
        name: "executionUser",
        type: "text",
        label: "Execution user",
        required: true,
        helpText: "A root worker runs the monitor's jobs as this non-root account, in a transient unit: one of the accounts known on its server.",
        showWhen: (vals) => isRootWorker(labelOf(vals["worker"])),
        render: ({ value, onChange, values }) => {
          const accounts = accountsFor(values);
          if (accounts.length === 0) {
            return createElement("div", { className: "dfr-help" }, `${labelOf(values["worker"])}'s runner allows no account to run jobs as: give its host policy one.`);
          }
          const cur = String(value ?? "");
          return createElement(
            "select",
            { value: cur, onChange: (e: { target: { value: string } }) => onChange(e.target.value), "data-testid": "execution-user" },
            ...(accounts.includes(cur) ? [] : [createElement("option", { key: "", value: "" }, "— select —")]),
            ...accounts.map((a) => createElement("option", { key: a, value: a }, a)),
          );
        },
      },
      {
        name: "name",
        type: "text",
        label: "Name",
        required: true,
        helpText: "Names the job, its schedule and the alert title.",
      },
      { name: "intervalMinutes", type: "number", label: "Every (minutes)", required: true },
      {
        name: "channel",
        type: "select",
        label: "Alert channel",
        required: true,
        options: LookupCache.getOptions(ALERT_CHANNEL_LOOKUP_KEY),
      },
      { name: "enabled", type: "toggle", label: "Enabled" },
      {
        name: "acceptWarnings",
        type: "toggle",
        label: "Save with warnings",
        helpText: "When Validate answers a warning for this configuration, tick to save it anyway.",
      },
    ];

    const initialValues: Record<string, unknown> = {
      worker: existing?.job?.["workeruid"] ?? "",
      executionUser: jobPayload.executionUser ?? "",
      name: existing?.job?.["name"] ?? "",
      intervalMinutes: Math.max(1, Math.round(interval / 60)),
      channel: Array.isArray(channels) ? (channels[0] ?? "") : "",
      enabled: existing ? Boolean(existing.monitor["enabled"]) : true,
      acceptWarnings: false,
    };
    configValues(plugin, extra.config, initialValues, loaded);
    // a new monitor like another: its values, apart from the name
    if (!existing && target.like) Object.assign(initialValues, target.like, { name: "", acceptWarnings: false });

    const validateAction: FormAction = {
      label: "Validate",
      run: async (values) => {
        let config: unknown;
        try {
          config = configOf(plugin, values, loaded);
        } catch (e) {
          return unassembled(e);
        }
        const down = noRunner(values);
        if (down) return { severity: "error", message: down };
        const worker = workerOf(values);
        let messages: PluginMessage[];
        try {
          messages = await validatePluginConfig(plugin, config, worker, `${plugin.name} monitor form: validate`);
        } catch (e) {
          return { severity: "error", message: `Validate did not answer on the worker: ${errorMessage(e) || String(e)}` };
        }
        validated = { json: JSON.stringify(config), messages };
        return validatedResult(plugin, messages, values);
      },
    };

    // collect once on the worker with the form's values, saving nothing: the readings show in the form
    const tryAction: FormAction = {
      label: "Try it",
      run: async (values) => {
        const collect = plugin.members["collect"];
        if (!collect) return { severity: "info", message: `${pluginTitle} has no collect to try.` };
        let config: unknown;
        try {
          config = configOf(plugin, values, loaded);
        } catch (e) {
          return unassembled(e);
        }
        const down = noRunner(values);
        if (down) return { severity: "error", message: down };
        const result = await runPluginFunction(plugin, collect.module, collect.function, config, workerOf(values), `${plugin.name} monitor form: try it`);
        return collectedLine(result);
      },
    };

    // the plugin is fixed in edit: a monitor of another plugin is a new monitor, seeded from this one
    const likeAction: FormAction = {
      label: "New monitor like this",
      run: async (values) => {
        showPluginMonitorForm({ filesetUid: plugin.filesetUid, fileset: target.fileset, plugin: plugin.name, like: values });
      },
    };

    // the configuration is its own section: generated from the plugin's type, apart from the monitor's own
    // fields; the module's form module, when the fileset carries one, shapes it from there
    const generated = (): FormSection[] => [{ title: `${pluginTitle} configuration`, items: configItems(plugin, types, secrets) }];
    let configSections = generated();
    let moduleActions: FormAction[] = [];
    if (plugin.form) {
      const moduleName = `${plugin.module}.form.ts`;
      try {
        const form = await importFormModule(plugin.form.js, moduleName);
        const spec = await form({
          plugin: { name: plugin.name, title: pluginTitle, module: plugin.module },
          type: plugin.config,
          prefix: CONFIG,
          item: (path) => `${CONFIG}.${path}`,
          generated,
          values: initialValues,
          editing: Boolean(existing),
          run: (module, fn, input, values) => {
            const down = noRunner(values);
            if (down) return Promise.reject(new Error(down));
            return runPluginFunction(plugin, module, fn, input, workerOf(values), `${plugin.name} monitor form: ${fn}`);
          },
          call: (module, fn, input) => callPluginFunction(loaded, module, fn, input),
        });
        configSections = spec.sections;
        moduleActions = spec.actions ?? [];
      } catch (e) {
        notify({ severity: "warning", title: `${moduleName} did not load`, message: `${errorMessage(e) || String(e)}. Showing the form generated from the type.` });
      }
    }

    // why the configuration has no defaults, when its completion failed: on its first section, after the
    // form module, which may build its own sections
    const note = defaultsNote(plugin.config);
    if (note && configSections[0] && !configSections[0].note) configSections[0] = { ...configSections[0], note };

    datasetFormRequest.next({
      title: existing ? `${pluginTitle} monitor` : `New ${pluginTitle} monitor`,
      sections: [{ title: "", items: formItems }, ...configSections],
      initialValues,
      submitLabel: "Save",
      actions: [validateAction, tryAction, ...(existing ? [likeAction] : []), ...moduleActions],
      onCancel: () => datasetFormRequest.next(undefined),
      onSubmit: async (values) => {
        // validate on save runs on the runner too; a worker with no runner would wait a minute to say nothing
        const down = noRunner(values) || accountProblem(values);
        if (down) return fail(down);
        await submit(target, plugin, existing, values, workerOf(values), labelOf(values["worker"]), validated, loaded);
      },
    });
  })();
}

async function submit(
  target: PluginMonitorFormTarget,
  plugin: RegisteredPlugin,
  existing: Existing | undefined,
  values: Record<string, unknown>,
  pluginWorker: PluginWorker,
  workerLabel: string,
  validated: { json: string; messages: PluginMessage[] },
  loaded: LoadedPlan | undefined,
) {
  const worker = pluginWorker.uid;
  const name = String(values["name"] ?? "").trim();
  const channel = String(values["channel"] ?? "");
  const enabled = Boolean(values["enabled"]);
  const minutes = Number(values["intervalMinutes"] ?? 5);
  if (!worker) return fail("Pick the worker the monitor collects from.");
  const sandbox = executionUserProblem(workerLabel, pluginWorker.executionUser);
  if (sandbox) return fail(sandbox);
  if (!name) return fail("Name the monitor.");
  if (!channel) return fail("Pick an alert channel.");
  if (!Number.isFinite(minutes) || minutes < 1) return fail("The interval is at least one minute.");

  let config: unknown;
  try {
    config = configOf(plugin, values, loaded);
  } catch (e) {
    return fail(`The configuration is not valid: ${(errorMessage(e) || String(e))}`);
  }
  // the configuration being saved is validated on the worker, whatever was shown before
  let messages = validated.messages;
  if (validated.json !== JSON.stringify(config)) {
    try {
      messages = await validatePluginConfig(plugin, config, pluginWorker, `${plugin.name} monitor form: validate`);
    } catch (e) {
      return fail(`Validate did not answer on the worker: ${(errorMessage(e) || String(e))}`);
    }
  }
  if (hasLevel(messages, "Error")) return fail("Validate found errors: " + messageLines(messages, "Error"));
  if (hasLevel(messages, "Warn") && !values["acceptWarnings"]) {
    return fail("Validate answered warnings: " + messageLines(messages, "Warn") + ". Tick Save with warnings to save anyway.");
  }

  const intervalSeconds = Math.round(minutes * 60);
  const fileset = target.fileset ?? target.filesetUid ?? plugin.filesetUid;
  await provisionMonitor({
    existingMonitorUid: target.monitorUid,
    existing,
    enabled,
    worker,
    monitorValues: {
      enabled,
      ...pluginColumns({ filesetUid: plugin.filesetUid, name: plugin.name }),
      channel_uids: JSON.stringify([channel]),
      expected_interval_seconds: intervalSeconds,
      grace_seconds: 120,
      alert_on_overdue: true,
      alert_on_nonzero_exit: false,
      alert_on_stderr: false,
      extra_data: pluginExtraData(existing?.monitor["extra_data"], config),
    },
    jobName: name,
    jobKind: "monitor-probe",
    jobDescription: `${name}: plugin ${plugin.name} of ${fileset}`,
    jobFilesetUid: plugin.filesetUid,
    payloadJson: pluginJobPayload(name, pluginWorker.executionUser),
    scheduleName: name,
    scheduleDescription: `${name}: plugin ${plugin.name} of ${fileset}`,
    scheduleExpr: `every ${minutes} minutes`,
    createdMessage: `${name} collects with ${plugin.title || plugin.name} every ${minutes} minutes.`,
    updatedMessage: `${name} updated.`,
  });
}
